import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Logo } from "@/components/Logo";
import { LanguagePill } from "@/components/LanguagePill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const googleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function Register() {
  const { t, lang } = useI18n();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register({ ...form, language: lang });
      toast.success("Account created!");
      navigate("/onboarding");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen hero-bg flex flex-col">
      <div className="flex items-center justify-between px-6 py-4">
        <Logo /><LanguagePill />
      </div>
      <div className="flex flex-1 items-center justify-center px-5 pb-10">
        <div className="w-full max-w-md rounded-3xl bg-white border border-slate-100 card-shadow-lg p-8">
          <h1 className="font-display text-3xl font-semibold text-navy text-center">{t("create_account")}</h1>
          <p className="text-center text-slate-500 mt-1 text-sm">{t("register_sub")}</p>

          <button data-testid="google-register-btn" onClick={googleLogin}
            className="mt-6 w-full flex items-center justify-center gap-3 rounded-full border border-slate-200 py-3 font-medium text-navy hover:bg-slate-50 transition-colors">
            <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="" className="h-5 w-5" />
            {t("continue_google")}
          </button>

          <div className="my-5 flex items-center gap-3 text-xs text-slate-400">
            <div className="h-px flex-1 bg-slate-200" /> {t("or")} <div className="h-px flex-1 bg-slate-200" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>{t("name")}</Label>
              <Input data-testid="register-name" required value={form.name} onChange={set("name")} className="mt-1.5 rounded-xl" />
            </div>
            <div>
              <Label>{t("email")}</Label>
              <Input data-testid="register-email" type="email" required value={form.email} onChange={set("email")} className="mt-1.5 rounded-xl" />
            </div>
            <div>
              <Label>{t("password")}</Label>
              <Input data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-1.5 rounded-xl" />
            </div>
            <Button data-testid="register-submit" type="submit" disabled={loading}
              className="w-full rounded-full brand-gradient text-white hover:opacity-90 py-6">
              {loading ? t("loading") : t("get_started")}
            </Button>
          </form>

          <p className="mt-5 text-center text-sm text-slate-500">
            {t("have_account")} <Link to="/login" className="font-semibold text-brandblue">{t("sign_in")}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
